import { useState } from 'react'
import { Eye } from 'lucide-react'
import { useVoiceStore } from '@/store/voice'
import { cx } from '@/lib/cx'
import type { AvatarDecoration } from '@/types/api'
import { Avatar } from '../Avatar'
import { ContextMenu, MenuTitle } from '../ContextMenu'
import styles from './StreamViewers.module.css'

/** Как показать зрителя: имя, аватар с рамкой, цвет заглушки. */
export interface Viewer {
  name: string
  avatar?: string | undefined
  decoration?: AvatarDecoration | null | undefined
  color: string
}

/**
 * Кто смотрит трансляцию, как у Discord: глаз с числом зрителей на её рамке, нажатие — список с аватарами.
 * Пока никто не смотрит, значка нет. who — имя и аватар по id: зрители — участники той же комнаты, их
 * знает тот, кто рисует плитку.
 */
export function StreamViewers({ streamerId, who, className }: { streamerId: string; who: (id: string) => Viewer; className?: string | undefined }) {
  const ids = useVoiceStore((s) => s.viewers[streamerId])
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  if (!ids?.length) return null
  const people = ids.map((id) => ({ id, ...who(id) }))
  const label = `Смотрят: ${people.map((p) => p.name).join(', ')}`
  return (
    <>
      <button
        type="button"
        className={cx(styles.badge, className)}
        data-tooltip={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={Boolean(menu)}
        // Иначе ContextMenu закроется на mousedown, а следующий click откроет его заново.
        onMouseDown={(e) => { if (menu) e.stopPropagation() }}
        // двойной щелчок по плитке разворачивает её — по значку он не должен
        onDoubleClick={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          const r = e.currentTarget.getBoundingClientRect()
          setMenu(menu ? null : { x: r.right, y: r.bottom + 4 })
        }}
      >
        <Eye size={14} aria-hidden="true" /> {ids.length}
      </button>
      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} align="right" label="Кто смотрит" onClose={() => setMenu(null)}>
          <MenuTitle>Смотрят — {ids.length}</MenuTitle>
          {people.map((p) => (
            <div key={p.id} className={styles.row}>
              <Avatar initial={p.name.slice(0, 1).toUpperCase()} color={p.color} size={24} src={p.avatar} decoration={p.decoration} />
              <span className={styles.name}>{p.name}</span>
            </div>
          ))}
        </ContextMenu>
      ) : null}
    </>
  )
}
